// 장바구니 저장/조작 로직. consent.ts와 같은 이유로 컴포넌트에서 분리했다:
// useCart.ts(React 훅)와 이 파일이 서로 다른 것을 import하지 않게 해
// 순환 참조 없이 양쪽에서 안전하게 쓸 수 있다.
//
// 저장하는 값은 상품 id/옵션 id/수량뿐이다. 가격이나 상품명은 절대 저장하지
// 않는다 — 장바구니를 오래 열어둔 사이 가격이 바뀌거나 상품이 품절될 수
// 있으므로, 실제 가격·재고는 항상 서버에서 다시 읽어와 검증한다
// (cart-validation.ts, 장바구니 페이지·주문서에서 사용).

const STORAGE_KEY = "cart_items";

// 주문 API(z.number().int().min(1).max(100))와 동일한 상한. 한 줄에 이보다
// 많이 담아도 어차피 서버가 거부하므로 클라이언트에서도 같은 상한을 둔다.
const MAX_LINE_QUANTITY = 100;

export interface CartLine {
  productId: string;
  optionId: string | null;
  quantity: number;
}

type Listener = () => void;

function isCartLine(value: unknown): value is CartLine {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.productId === "string" &&
    entry.productId.length > 0 &&
    (entry.optionId === null || typeof entry.optionId === "string") &&
    typeof entry.quantity === "number" &&
    Number.isInteger(entry.quantity) &&
    entry.quantity > 0
  );
}

// localStorage.getItem과 JSON.parse는 서로 다른 이유로 던질 수 있는
// (스토리지 접근 자체가 막힌 경우 vs. 저장된 값이 깨진 JSON인 경우) 별개의
// throwable이라 각각 따로 감싼다.
function readFromStorage(): CartLine[] {
  const raw = (() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  })();
  if (!raw) return [];

  const parsed: unknown = (() => {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  })();
  if (!Array.isArray(parsed)) return [];

  return parsed.filter(isCartLine);
}

function writeToStorage(lines: CartLine[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  } catch {
    // 프라이빗 모드·저장 공간 초과 — consent.ts와 동일하게 조용히 무시.
  }
}

// 모듈 스코프 캐시 + 구독자 목록. useSyncExternalStore가 매 렌더마다 같은
// 배열 참조를 돌려받아야 불필요한 재렌더가 없으므로, 실제로 바뀔 때만
// 새 배열을 만든다.
let cachedLines: CartLine[] | null = null;
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of listeners) listener();
}

function setLines(next: CartLine[]): void {
  cachedLines = next;
  writeToStorage(next);
  notify();
}

export function getCartSnapshot(): CartLine[] {
  if (cachedLines === null) {
    cachedLines = readFromStorage();
  }
  return cachedLines;
}

// useSyncExternalStore는 이 함수가 매번 같은 참조를 돌려주길 기대한다
// (그렇지 않으면 "The result of getServerSnapshot should be cached" 경고와
// 함께 하이드레이션 중 불필요한 재렌더가 발생한다). 매번 새 배열 리터럴을
// 돌려주면 그 계약을 어기게 되므로, 모듈 스코프 상수 하나를 재사용한다.
//
// SSR·최초 하이드레이션에서는 이 빈 장바구니로 렌더링하고, 마운트 후 실제
// 로컬스토리지 값으로 전환된다(CookieBanner.tsx와 같은 패턴).
const EMPTY_CART: CartLine[] = [];

export function getServerCartSnapshot(): CartLine[] {
  return EMPTY_CART;
}

export function subscribeCart(listener: Listener): () => void {
  listeners.add(listener);
  // 다른 탭에서 장바구니를 바꾼 경우(storage 이벤트는 같은 탭에서는 발생하지
  // 않으므로, 이 탭 안의 변경은 setLines()의 notify()가 처리한다).
  function onStorage(e: StorageEvent) {
    if (e.key !== STORAGE_KEY) return;
    cachedLines = readFromStorage();
    listener();
  }
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

function lineKey(productId: string, optionId: string | null): string {
  return `${productId}:${optionId ?? ""}`;
}

// 같은 상품+옵션 조합이 이미 있으면 수량을 더하고(중복 줄 생성 방지),
// 없으면 새 줄을 추가한다.
export function addToCart(
  productId: string,
  optionId: string | null,
  quantity: number,
): void {
  const current = getCartSnapshot();
  const key = lineKey(productId, optionId);
  const existing = current.find(
    (line) => lineKey(line.productId, line.optionId) === key,
  );
  const next = existing
    ? current.map((line) =>
        lineKey(line.productId, line.optionId) === key
          ? {
              ...line,
              quantity: Math.min(MAX_LINE_QUANTITY, line.quantity + quantity),
            }
          : line,
      )
    : [
        ...current,
        {
          productId,
          optionId,
          quantity: Math.min(MAX_LINE_QUANTITY, Math.max(1, quantity)),
        },
      ];
  setLines(next);
}

export function updateCartQuantity(
  productId: string,
  optionId: string | null,
  quantity: number,
): void {
  const key = lineKey(productId, optionId);
  const next = getCartSnapshot().map((line) =>
    lineKey(line.productId, line.optionId) === key
      ? {
          ...line,
          quantity: Math.min(MAX_LINE_QUANTITY, Math.max(1, quantity)),
        }
      : line,
  );
  setLines(next);
}

export function removeFromCart(
  productId: string,
  optionId: string | null,
): void {
  const key = lineKey(productId, optionId);
  setLines(
    getCartSnapshot().filter(
      (line) => lineKey(line.productId, line.optionId) !== key,
    ),
  );
}

export function clearCart(): void {
  setLines([]);
}

// 헤더 뱃지용. 상품별 최소 주문 수량(minOrderQuantity)이 있어 수량 합계를
// 보여주면 "품목 1개 담았는데 뱃지엔 5"처럼 보인다. 그래서 담긴 줄(품목)
// 수를 센다. 수량 합계가 필요한 곳(장바구니 페이지·주문서 합계 등)은 각자
// line.quantity를 직접 합산하므로 이 함수와 무관하다.
export function getCartLineCount(lines: CartLine[]): number {
  return lines.length;
}
